import { PendingButton } from "@/components/common/PendingButton/PendingButton"
import { MainLayout } from "@/components/layout/MainLayout/MainLayout"
import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  useBillingStatus,
  type BillingInterval,
  type BillingPlanId,
} from "@/services/api/billingService"
import { clearUpgradeAfterSignIn } from "@/utils/upgradeEntry"
import { refreshAccount } from "@/utils/upgradeSession"
import { URLS } from "@/utils/urls.const"
import { BadgeCheck, Clock } from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import { useSearchParams } from "react-router"
import { CheckoutLayout } from "./CheckoutLayout"
import { Activation, CheckoutResult } from "./CheckoutResult"
import { CheckoutStep } from "./CheckoutStep"
import { ManagePlanButton } from "./ManagePlanButton"
import { PlanPicker } from "./PlanPicker"
import { StepUpButton } from "./StepUpButton"
import { UpgradeBreadcrumb } from "./UpgradeBreadcrumb"
import {
  UpgradeError,
  UpgradeHeading,
  UpgradeSkeleton,
  UpgradeState,
} from "./UpgradeStates"

type Step =
  | { name: "pick" }
  | { name: "checkout"; plan: BillingPlanId }
  | { name: "result"; sessionId: string | null; plan?: BillingPlanId }

/** /upgrade: plan picker, then Stripe Embedded Checkout on the same page. */
export const UpgradePage: React.FC = () => {
  const { translate } = useTranslation()
  const { data, isPending, isError, isFetching, refetch } = useBillingStatus()
  const [searchParams] = useSearchParams()
  // Back from Stripe's step up page: the plan changes with the webhook, which
  // may not be here yet, so the page must not offer the step up again
  const steppedUp = searchParams.get("step_up") === "done"
  const [interval, setInterval] = useState<BillingInterval>("month")
  const [step, setStep] = useState<Step>({ name: "pick" })
  const [alreadySubscribed, setAlreadySubscribed] = useState(false)
  const [checkoutFailed, setCheckoutFailed] = useState(false)
  // A payment is running: the breadcrumb must not lead away from its result
  const [paying, setPaying] = useState(false)
  // Arrived: a pending return from a Google sign-in is done
  useEffect(clearUpgradeAfterSignIn, [])
  const onPaidPlan = data?.hasActiveSubscription && !steppedUp
  useEffect(() => {
    if (onPaidPlan) void refreshAccount()
  }, [onPaidPlan])

  const backToPlans = useCallback(() => {
    setCheckoutFailed(false)
    setPaying(false)
    setStep({ name: "pick" })
  }, [])
  const handleComplete = useCallback(
    (sessionId: string, plan: BillingPlanId) => {
      // The form unmounts mid-payment, so it never reports the end itself
      setPaying(false)
      setStep({ name: "result", sessionId, plan })
    },
    [],
  )
  // The cached status predates the 409 (e.g. another tab just paid): reload it so the panel offers the
  // portal to a fresh Stripe subscriber instead of the support contact.
  const handleAlreadySubscribed = useCallback(() => {
    setAlreadySubscribed(true)
    void refetch()
  }, [refetch])
  const handleCheckoutError = useCallback(() => setCheckoutFailed(true), [])
  // The stage stopped selling (e.g. during a deploy): the support contact, whatever a later status says
  const [notConfigured, setNotConfigured] = useState(false)
  const handleNotConfigured = useCallback(() => setNotConfigured(true), [])

  const retry = (
    <PendingButton
      variant="outline"
      size="sm"
      pending={isFetching}
      onClick={() => void refetch()}
    >
      {translate("UPGRADE/RETRY")}
    </PendingButton>
  )
  // Loading and load errors keep the picker's heading, so the page does not jump when the plans arrive
  const pickerFrame = (body: React.ReactNode) => (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
      <UpgradeHeading
        title={translate("UPGRADE/TITLE")}
        description={translate("UPGRADE/SUBTITLE")}
      />
      {body}
    </div>
  )

  // A failed refetch keeps its data: an open checkout and the wait after it
  // survive that, any other screen would act on a stale status
  const midPurchase =
    step.name === "result" || (step.name === "checkout" && !alreadySubscribed)

  let content: React.ReactNode
  if (steppedUp) {
    content = <Activation plan="pro" stepUp />
  } else if (isPending) {
    content = pickerFrame(<UpgradeSkeleton />)
  } else if (!data || (isError && !midPurchase)) {
    content = pickerFrame(
      <UpgradeError
        description={translate("UPGRADE/ERROR_DESCRIPTION")}
        action={retry}
      />,
    )
  } else if (step.name === "result") {
    content = (
      <CheckoutResult
        sessionId={step.sessionId}
        onBackToPlans={backToPlans}
        plan={step.plan}
        confirmed
      />
    )
  } else if (data.canStepUp) {
    content = (
      <UpgradeState
        icon={<BadgeCheck className="size-6 text-success" aria-hidden="true" />}
        title={translate("UPGRADE/STEP_UP_TITLE")}
        description={translate("UPGRADE/STEP_UP_DESCRIPTION")}
        actions={
          <>
            <StepUpButton />
            <ManagePlanButton variant="outline" />
          </>
        }
      />
    )
  } else if (data.paidByTeammate) {
    content = (
      <UpgradeState
        icon={<BadgeCheck className="size-6 text-success" aria-hidden="true" />}
        title={translate("UPGRADE/ALREADY_SUBSCRIBED_TITLE")}
        description={translate("UPGRADE/TEAM_PLAN_DESCRIPTION")}
        actions={
          <Button variant="outline" asChild>
            <a href={URLS.INFO_BOX.HELP}>{translate("TEAM/CONTACT_US")}</a>
          </Button>
        }
      />
    )
  } else if (alreadySubscribed || data.hasActiveSubscription) {
    content = (
      <UpgradeState
        icon={<BadgeCheck className="size-6 text-success" aria-hidden="true" />}
        title={translate("UPGRADE/ALREADY_SUBSCRIBED_TITLE")}
        description={translate(
          data.canManageBilling
            ? "UPGRADE/ALREADY_SUBSCRIBED_DESCRIPTION"
            : "UPGRADE/MANAGED_PLAN_DESCRIPTION",
        )}
        actions={
          data.canManageBilling ? (
            <ManagePlanButton />
          ) : (
            // Sales-led plan without a Stripe customer: the portal would 404
            <Button asChild>
              {/* Support, not the upgrade page: its payment links would sell this user a second plan. */}
              <a href={URLS.INFO_BOX.HELP}>{translate("TEAM/CONTACT_US")}</a>
            </Button>
          )
        }
      />
    )
  } else if (!data.checkoutEnabled || notConfigured) {
    // The stage cannot sell (missing key or config, already alarmed): subscribers above still reach the portal
    content = (
      <UpgradeState
        icon={<Clock className="size-6" aria-hidden="true" />}
        title={translate("UPGRADE/UNAVAILABLE_TITLE")}
        description={translate("UPGRADE/UNAVAILABLE_DESCRIPTION")}
        actions={
          <Button asChild>
            <a href={URLS.INFO_BOX.HELP}>{translate("TEAM/CONTACT_US")}</a>
          </Button>
        }
      />
    )
  } else if (step.name === "checkout" && data.publishableKey) {
    const chosenPlan = step.plan
    content = checkoutFailed ? (
      <CheckoutLayout onBack={backToPlans}>
        <UpgradeError
          description={translate("UPGRADE/ERROR_DESCRIPTION")}
          action={
            <Button variant="outline" size="sm" onClick={backToPlans}>
              {translate("UPGRADE/BACK_TO_PLANS")}
            </Button>
          }
        />
      </CheckoutLayout>
    ) : (
      <CheckoutStep
        // A new plan or interval starts a new checkout session
        key={`${step.plan}-${interval}`}
        publishableKey={data.publishableKey}
        request={{ plan: step.plan, interval }}
        onBack={backToPlans}
        onComplete={(sessionId) => handleComplete(sessionId, chosenPlan)}
        onAlreadySubscribed={handleAlreadySubscribed}
        onNotConfigured={handleNotConfigured}
        onError={handleCheckoutError}
        onPayingChange={setPaying}
      />
    )
  } else if (data.plans.length === 0) {
    content = pickerFrame(
      <UpgradeError
        description={translate("UPGRADE/ERROR_DESCRIPTION")}
        action={retry}
      />,
    )
  } else {
    content = (
      <div className="mx-auto w-full max-w-5xl">
        <PlanPicker
          plans={data.plans}
          interval={interval}
          onIntervalChange={setInterval}
          onSelect={(plan) => setStep({ name: "checkout", plan })}
        />
      </div>
    )
  }

  return (
    <MainLayout
      headerLeftElement={
        <UpgradeBreadcrumb
          plan={step.name === "checkout" ? step.plan : undefined}
          onBackToPlans={
            step.name === "checkout" && paying ? undefined : backToPlans
          }
        />
      }
      contentCenter={
        <div data-testid="upgrade-page" className="h-full">
          {content}
        </div>
      }
    />
  )
}
